import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationAudience, NotificationChannel, NotificationEventType, NotificationRule } from '@central/contracts';
import { alterarNotificacao, chavesDeAdministracao, listarNotificacoes } from '@/features/admin/admin.api';

const ROTULO_AUDIENCIA: Record<NotificationAudience, string> = {
  CLIENT: 'Cliente',
  OPERATOR: 'Operador',
  WATCHER: 'Observador',
};

function useRegraAtual(
  rules: NotificationRule[],
  eventType: string,
  audience: NotificationAudience,
  channel: NotificationChannel,
): boolean {
  const regra = rules.find(
    (r) => r.eventType === eventType && r.audience === audience && r.channel === channel,
  );
  return regra?.enabled ?? true;
}

function LinhaDeNotificacao({
  tipo,
  audience,
  rules,
}: {
  tipo: NotificationEventType;
  audience: NotificationAudience;
  rules: NotificationRule[];
}) {
  const queryClient = useQueryClient();
  const habilitado = useRegraAtual(rules, tipo.eventType, audience, 'EMAIL');

  const { mutate, isPending } = useMutation({
    mutationFn: (enabled: boolean) =>
      alterarNotificacao(tipo.eventType, audience, 'EMAIL', enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDeAdministracao.notificacoes });
    },
  });

  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">{tipo.label}</span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {ROTULO_AUDIENCIA[audience]}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{tipo.description}</p>
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={habilitado}
        disabled={isPending}
        onClick={() => mutate(!habilitado)}
        className={[
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
          'transition-colors duration-200 focus-visible:outline-none focus-visible:shadow-glow',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          habilitado ? 'bg-primary' : 'bg-muted-foreground/30',
        ].join(' ')}
      >
        <span
          className={[
            'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow',
            'transform transition duration-200',
            habilitado ? 'translate-x-4' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

/**
 * Painel de controle de notificações por e-mail.
 *
 * O design é opt-out: todos habilitados por padrão, o operador desativa
 * individualmente o que estiver poluindo. Sem regra no banco = habilitado.
 */
export function NotificationsPage() {
  const consulta = useQuery({
    queryKey: chavesDeAdministracao.notificacoes,
    queryFn: listarNotificacoes,
  });

  const catalog = consulta.data?.catalog ?? [];
  const rules = consulta.data?.rules ?? [];

  if (consulta.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <p className="text-sm text-destructive">
        Não foi possível carregar as regras de notificação.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">E-mail</h2>
          <p className="text-xs text-muted-foreground">
            Habilitado por padrão. Desative individualmente os que estiverem gerando ruído.
          </p>
        </div>

        <div className="divide-y divide-border px-4">
          {catalog.map((tipo) =>
            tipo.audiences.map((audience) => (
              <LinhaDeNotificacao
                key={`${tipo.eventType}-${audience}`}
                tipo={tipo}
                audience={audience}
                rules={rules}
              />
            )),
          )}
        </div>
      </div>
    </div>
  );
}
